/**
 * Types for the Portal Notifications feature (IVS-1040) — admin-authored
 * notifications delivered to Client Portal users via the login pop-up and
 * the Notification Hub.
 *
 * Unlike Broadcasts (Firestore-backed history), portal notifications are
 * owned by iv3api's `portal_notifications` tables — this module is a pure
 * CRUD UI over the admin endpoints. Mirrors the payload contract in
 * `iv3api/docs/portal-notifications/PORTAL_NOTIFICATIONS.md`.
 */
import type { BroadcastEnv } from "./broadcasts";

export type { BroadcastEnv };

/** Authorable categories. SYSTEM is reserved for the legacy client-side feed. */
export type PortalNotificationCategory = "PRODUCT" | "NEWS" | "INSTITUTE";

/** Full proto enum name the iv3api gateway expects. */
export const PORTAL_CATEGORY_TO_PROTO: Record<
  PortalNotificationCategory,
  string
> = {
  PRODUCT: "PORTAL_NOTIFICATION_CATEGORY_PRODUCT",
  NEWS: "PORTAL_NOTIFICATION_CATEGORY_NEWS",
  INSTITUTE: "PORTAL_NOTIFICATION_CATEGORY_INSTITUTE",
};

const PROTO_TO_PORTAL_CATEGORY: Record<string, PortalNotificationCategory> = {
  PORTAL_NOTIFICATION_CATEGORY_PRODUCT: "PRODUCT",
  PORTAL_NOTIFICATION_CATEGORY_NEWS: "NEWS",
  PORTAL_NOTIFICATION_CATEGORY_INSTITUTE: "INSTITUTE",
};

export function portalCategoryFromProto(
  value: string,
): PortalNotificationCategory {
  return PROTO_TO_PORTAL_CATEGORY[value] ?? "NEWS";
}

export type PortalNotificationStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export const PORTAL_STATUS_TO_PROTO: Record<PortalNotificationStatus, string> =
  {
    DRAFT: "PORTAL_NOTIFICATION_STATUS_DRAFT",
    PUBLISHED: "PORTAL_NOTIFICATION_STATUS_PUBLISHED",
    ARCHIVED: "PORTAL_NOTIFICATION_STATUS_ARCHIVED",
  };

export function portalStatusFromProto(value: string): PortalNotificationStatus {
  if (value.endsWith("_PUBLISHED")) return "PUBLISHED";
  if (value.endsWith("_ARCHIVED")) return "ARCHIVED";
  return "DRAFT";
}

export type PortalNotificationTargeting =
  "ALL_COMPANIES" | "SPECIFIC_COMPANIES";

export const PORTAL_TARGETING_TO_PROTO: Record<
  PortalNotificationTargeting,
  string
> = {
  ALL_COMPANIES: "PORTAL_NOTIFICATION_TARGETING_ALL_COMPANIES",
  SPECIFIC_COMPANIES: "PORTAL_NOTIFICATION_TARGETING_SPECIFIC_COMPANIES",
};

export function portalTargetingFromProto(
  value: string,
): PortalNotificationTargeting {
  return value.endsWith("_SPECIFIC_COMPANIES")
    ? "SPECIFIC_COMPANIES"
    : "ALL_COMPANIES";
}

/**
 * A portal notification as returned by the admin API. int64 fields arrive
 * as JSON strings (protojson) — normalize with `toMillis` before use.
 */
export interface PortalNotification {
  id: string;
  headline: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  category: string;
  status: string;
  targeting: string;
  targetCompanyIds: string[];
  startAtUnixMillis: string | number;
  endAtUnixMillis: string | number;
  publishedAtUnixMillis: string | number;
  createdBy: string;
  createdAtUnixMillis: string | number;
  updatedAtUnixMillis: string | number;
}

/** Request body for create/update (PortalNotificationInput proto message). */
export interface PortalNotificationInputBody {
  headline: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  category: string;
  targeting: string;
  targetCompanyIds: string[];
  startAtUnixMillis: number;
  endAtUnixMillis: number;
}

export interface PageMeta {
  pageSize: string | number;
  pageNumber: string | number;
  totalPages: string | number;
  totalElements: string | number;
}

export interface ListPortalNotificationsResponse {
  notifications: PortalNotification[];
  meta?: PageMeta;
}

/** One company row from /api/v2/companies/operator/search. */
export interface CompanySearchResult {
  id: string;
  name: string;
  shortcode?: string;
}

export interface CompanySearchResponse {
  companies: CompanySearchResult[];
  meta?: PageMeta;
}

/** Composer form state — what the UI binds to. */
export interface PortalNotificationFormState {
  env: BroadcastEnv;
  category: PortalNotificationCategory;
  headline: string;
  body: string;
  /** Empty string = no CTA. */
  ctaLabel: string;
  ctaUrl: string;
  /** `YYYY-MM-DDTHH:mm` local datetime strings (datetime-local format). */
  startAtLocal: string;
  endAtLocal: string;
  targeting: PortalNotificationTargeting;
  /** Selected companies for SPECIFIC_COMPANIES targeting. */
  targetCompanies: CompanySearchResult[];
}
